// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	createResource,
	useResource,
	type RouteResource,
	type UseResourceHookResponse,
} from '@atlassian/react-resource-router';

import { QUERY } from './RovoEntitlementQuery.tsx';

type RovoEntitlementGraphQLResponse = {
	data: {
		jira: {
			isRovoEnabled: boolean;
		} | null;

		jira_isRovoLLMEnabled?: boolean;
		jira_isBetaAiFeaturesEnabled?: boolean;
	} | null;
	errors: Array<{ message?: string }>;
};

const RESOURCE_TYPE_ROVO_ENTITLEMENT = 'ROVO_ENTITLEMENT';

type RovoEntitlementData = {
	isRovoEnabled: boolean | null;
	isRovoLLMEnabled: boolean | null | undefined;
	isBetaAiFeaturesEnabled: boolean | null | undefined;
};

const fetchRovoEntitlement = async (cloudId: string): Promise<RovoEntitlementData> => {
	try {
		const response = await performPostRequest<RovoEntitlementGraphQLResponse>(
			'/gateway/api/graphql',
			{
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
						shouldIncludeBetaAiFeatures: true,
					},
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		// handle GraphQL errors
		if (response.errors) {
			fireErrorAnalytics({
				meta: {
					id: 'navigationResource',
					packageName: 'jiraRouterResourcesRovoEntitlement',
					teamName: 'ai-mate',
				},
				error: new Error(
					`RovoEntitlement Error. ${response.errors.map((error) => error.message).join('; ')}; IsDataEmpty=${response.data?.jira === null || response.data?.jira === undefined}`,
				),
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		const isRovoEnabled = response.data?.jira?.isRovoEnabled ?? null;
		const isRovoLLMEnabled = response.data?.jira_isRovoLLMEnabled ?? null;
		const isBetaAiFeaturesEnabled = response.data?.jira_isBetaAiFeaturesEnabled ?? null;

		return {
			isRovoEnabled,
			isRovoLLMEnabled,
			isBetaAiFeaturesEnabled,
		};
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'navigationResource',
				packageName: 'jiraRouterResourcesRovoEntitlement',
				teamName: 'ai-mate',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	return {
		isRovoEnabled: null,
		isRovoLLMEnabled: undefined,
		isBetaAiFeaturesEnabled: undefined,
	};
};

export const rovoEntitlementResource: RouteResource<RovoEntitlementData> =
	createResource<RovoEntitlementData>({
		type: RESOURCE_TYPE_ROVO_ENTITLEMENT,
		getKey: () => RESOURCE_TYPE_ROVO_ENTITLEMENT,
		getData: (_, { tenantContext: { cloudId } }) => fetchRovoEntitlement(cloudId),
		maxAge: Infinity,
		isCritical: true,
	});

export const useRovoEntitlementResource = (): UseResourceHookResponse<RovoEntitlementData> =>
	useResource(rovoEntitlementResource);
